import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RdsAccordionDirective,
  RdsButtonComponent,
  RdsExpansionPanelComponent,
  RdsExpansionPanelHeaderComponent,
  RdsExpansionPanelTitleDirective,
  RdsFormFieldComponent,
  RdsInputDirective,
  RdsLabelDirective,
  RdsMultiSelectComponent,
  RdsMultiSelectOptionComponent,
  RdsProgressSpinnerComponent,
  RdsSearchComponent,
} from '@rds/angular-components';

import { RoleType, ViewportService } from '@bookly/shared';

import { RoomsListTableComponent } from '../../components/rooms-list-table/rooms-list-table.component';
import { RoomFilters, RoomsService } from '../../services/rooms.service';

@Component({
  selector: 'bad-rooms-list',
  imports: [
    RoomsListTableComponent,
    ReactiveFormsModule,
    RdsSearchComponent,
    RdsFormFieldComponent,
    RdsLabelDirective,
    RdsMultiSelectComponent,
    RdsMultiSelectOptionComponent,
    RdsInputDirective,
    RdsButtonComponent,
    RdsProgressSpinnerComponent,
    RdsAccordionDirective,
    RdsExpansionPanelComponent,
    RdsExpansionPanelHeaderComponent,
    RdsExpansionPanelTitleDirective,
  ],
  templateUrl: './rooms-list.component.html',
  styleUrl: './rooms-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomsListComponent implements OnInit, OnDestroy {
  readonly #roomsService = inject(RoomsService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);

  /**
   * Collapsed by default where the filters would otherwise push the content off
   * screen. Seeded once, so resizing does not fight the user's own toggling.
   */
  protected readonly filtersExpanded = signal(
    !inject(ViewportService).isCompact()
  );

  protected readonly filteredRooms = this.#roomsService.filteredRooms;
  protected readonly siteNames = this.#roomsService.siteNames;
  protected readonly buildingNames = this.#roomsService.buildingNames;
  protected readonly floorNames = this.#roomsService.floorNames;
  protected readonly roomTypes = this.#roomsService.roomTypes;
  protected readonly isRoomListLoading = this.#roomsService.isRoomListLoading;
  protected role: RoleType | null = null;
  protected filterForm: FormGroup = this.#fb.group({
    roomName: [''],
    capacity: [null],
    siteName: [[]],
    buildingName: [[]],
    floorName: [[]],
    roomType: [[]],
  });

  ngOnInit() {
    this.role = this.#activatedRoute.snapshot.data['role'] as RoleType | null;
    this.#roomsService.getRooms(this.role).subscribe(() => {
      this.#initFiltersFromQueryParams();
      this.#subscribeToFilterChanges();
    });
  }

  ngOnDestroy() {
    this.#roomsService.clearRoomFilters();
  }

  protected clearFilters() {
    this.filterForm.reset({
      roomName: '',
      capacity: null,
      siteName: [],
      buildingName: [],
      floorName: [],
      roomType: [],
    });
    this.#roomsService.clearRoomFilters();
    this.#clearFilterQueryParams();
  }

  protected openRoomDetails(data: { roomDexId: string }) {
    this.#router.navigate([data.roomDexId], {
      relativeTo: this.#activatedRoute,
    });
  }

  #paramToArray(value: string | null): string[] {
    return value ? value.split(',').filter(Boolean) : [];
  }

  #arrayToParam(value: string[] | undefined): string | null {
    return value?.length ? value.join(',') : null;
  }

  #initFiltersFromQueryParams() {
    const queryParams = this.#activatedRoute.snapshot.queryParamMap;
    const capacityParam = queryParams.get('capacity');
    const filters: RoomFilters = {
      roomName: queryParams.get('roomName') || '',
      capacity: capacityParam ? +capacityParam : undefined,
      siteName: this.#paramToArray(queryParams.get('siteName')),
      buildingName: this.#paramToArray(queryParams.get('buildingName')),
      floorName: this.#paramToArray(queryParams.get('floorName')),
      roomType: this.#paramToArray(queryParams.get('roomType')),
    };

    const hasFilters =
      filters.roomName ||
      filters.capacity ||
      (filters.siteName && filters.siteName.length > 0) ||
      (filters.buildingName && filters.buildingName.length > 0) ||
      (filters.floorName && filters.floorName.length > 0) ||
      (filters.roomType && filters.roomType.length > 0);

    if (hasFilters) {
      this.filterForm.patchValue(filters, { emitEvent: false });
      this.#roomsService.setRoomFilters(filters);
    }
  }

  #updateUrlWithFilters() {
    const formValue = this.filterForm.value;
    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams: {
        roomName: formValue.roomName || null,
        capacity: formValue.capacity || null,
        siteName: this.#arrayToParam(formValue.siteName),
        buildingName: this.#arrayToParam(formValue.buildingName),
        floorName: this.#arrayToParam(formValue.floorName),
        roomType: this.#arrayToParam(formValue.roomType),
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  #clearFilterQueryParams() {
    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams: {
        roomName: null,
        capacity: null,
        siteName: null,
        buildingName: null,
        floorName: null,
        roomType: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  #subscribeToFilterChanges() {
    this.filterForm
      .get('siteName')!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const currentBuildings = this.buildingNames();
        const selectedBuildings: string[] =
          this.filterForm.get('buildingName')!.value || [];
        const validBuildings = selectedBuildings.filter(b =>
          currentBuildings.includes(b)
        );
        if (validBuildings.length !== selectedBuildings.length) {
          this.filterForm
            .get('buildingName')!
            .setValue(validBuildings, { emitEvent: true });
        }
      });

    this.filterForm
      .get('buildingName')!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const currentFloors = this.floorNames();
        const selectedFloors: string[] =
          this.filterForm.get('floorName')!.value || [];
        const validFloors = selectedFloors.filter(f =>
          currentFloors.includes(f)
        );
        if (validFloors.length !== selectedFloors.length) {
          this.filterForm
            .get('floorName')!
            .setValue(validFloors, { emitEvent: true });
        }
      });

    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(values => {
        const filters: RoomFilters = {};

        if (values.roomName) filters.roomName = values.roomName;
        if (values.capacity) filters.capacity = values.capacity;
        if (values.siteName?.length) filters.siteName = values.siteName;
        if (values.buildingName?.length)
          filters.buildingName = values.buildingName;
        if (values.floorName?.length) filters.floorName = values.floorName;
        if (values.roomType?.length) filters.roomType = values.roomType;

        this.#roomsService.setRoomFilters(filters);
        this.#updateUrlWithFilters();
      });
  }
}
